// src/services/apiService.js - Versión completa y actualizada

/**
 * Servicio para realizar solicitudes HTTP a la API
 * Gestiona autenticación, caché, errores y modos de depuración
 */

// -----------------------------------------------------------------------------
// CONFIGURACIÓN
// -----------------------------------------------------------------------------

// Modo de depuración para simular respuestas y evitar llamadas reales a la API
const API_DEBUG_MODE = false; // Cambiar a true para activar simulación de datos

// URL base de la API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Timeout predeterminado para las solicitudes (en ms)
const DEFAULT_TIMEOUT = 15000; // 15 segundos

// Configuración de caché
const apiCache = new Map();
const API_CACHE_TIME = {
    default: 60000, // 1 minuto para la mayoría de los datos
    clients: 30000,  // 30 segundos para clientes
    traffic: 5000,   // 5 segundos para datos de tráfico
    dashboard: 60000 // 1 minuto para datos de dashboard
};

// -----------------------------------------------------------------------------
// FUNCIONES DE UTILIDAD
// -----------------------------------------------------------------------------

/**
 * Agrega timeout a una promesa fetch
 * @param {Promise} fetchPromise - Promesa original de fetch
 * @param {number} timeout - Tiempo en ms para timeout
 * @returns {Promise} - Promesa con timeout
 */
const timeoutPromise = (fetchPromise, timeout) => {
    return Promise.race([
        fetchPromise,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`La solicitud excedió el tiempo límite (${timeout}ms)`)), timeout)
        )
    ]);
};

/**
 * Limpia la caché para una clave específica o todos los elementos
 * @param {string} cacheKey - Clave específica para limpiar (opcional)
 */
export const clearCache = (cacheKey = null) => {
    if (cacheKey) {
        console.log(`Limpiando caché para: ${cacheKey}`);
        apiCache.delete(cacheKey);
    } else {
        console.log('Limpiando toda la caché de API');
        apiCache.clear();
    }
};

/**
 * Verifica si hay un token de cliente válido
 * @returns {boolean} - Verdadero si hay un token válido
 */
export const hasValidClientToken = () => {
    const token = localStorage.getItem('clientToken');
    return !!token && token.length > 20; // Un token JWT suele ser más largo que esto
};

/**
 * Verifica si hay un token de administrador válido
 * @returns {boolean} - Verdadero si hay un token válido
 */
export const hasValidAdminToken = () => {
    const token = localStorage.getItem('token');
    return !!token && token.length > 20; // Un token JWT suele ser más largo que esto
};

/**
 * Función para simular datos de API en modo de depuración
 * @param {string} endpoint - Endpoint para el que simular datos
 * @returns {Object} - Datos simulados
 */
export const simulateFetchData = (endpoint) => {
    console.log(`[API DEBUG] Simulando datos para: ${endpoint}`);

    // Dashboard
    if (endpoint.includes('/dashboard')) {
        return {
            clientCount: 24,
            activeClients: 22,
            pendingInvoices: 8,
            recentPayments: 12,
            ticketsOpen: 3,
            message: "[DATOS SIMULADOS]"
        };
    }

    // Clientes
    if (endpoint.includes('/clients')) {
        return {
            data: [
                { id: 'client1', firstName: 'Juan', lastName: 'Pérez', email: 'juan@example.com', status: 'active' },
                { id: 'client2', firstName: 'María', lastName: 'González', email: 'maria@example.com', status: 'active' },
                { id: 'client3', firstName: 'Carlos', lastName: 'Rodríguez', email: 'carlos@example.com', status: 'suspended' },
                { id: 'client4', firstName: 'Ana', lastName: 'López', email: 'ana@example.com', status: 'active' }
            ],
            totalCount: 24,
            page: 1,
            pageSize: 10
        };
    }

    // Facturas
    if (endpoint.includes('/invoices')) {
        return {
            data: [
                { id: 'inv1', clientId: 'client1', clientName: 'Juan Pérez', amount: 70.00, status: 'pending', dueDate: '2025-05-15' },
                { id: 'inv2', clientId: 'client2', clientName: 'María González', amount: 50.00, status: 'paid', dueDate: '2025-05-10' },
                { id: 'inv3', clientId: 'client3', clientName: 'Carlos Rodríguez', amount: 100.00, status: 'overdue', dueDate: '2025-05-05' },
                { id: 'inv4', clientId: 'client4', clientName: 'Ana López', amount: 70.00, status: 'pending', dueDate: '2025-05-20' }
            ],
            totalCount: 56,
            page: 1,
            pageSize: 10
        };
    }

    // Pagos
    if (endpoint.includes('/payments')) {
        return {
            data: [
                { id: 'pay1', invoiceId: 'inv2', clientName: 'María González', amount: 50.00, date: '2025-05-08', method: 'transfer' },
                { id: 'pay2', invoiceId: 'inv4', clientName: 'Ana López', amount: 70.00, date: '2025-05-01', method: 'card' }
            ],
            totalCount: 42,
            page: 1,
            pageSize: 10
        };
    }

    // Tickets de soporte
    if (endpoint.includes('/support')) {
        return {
            data: [
                { id: 'tick1', clientId: 'client1', subject: 'Problema de conexión', status: 'open', priority: 'high', createdAt: '2025-05-07T10:30:00Z' },
                { id: 'tick2', clientId: 'client3', subject: 'Facturación incorrecta', status: 'in_progress', priority: 'medium', createdAt: '2025-05-06T14:45:00Z' },
                { id: 'tick3', clientId: 'client2', subject: 'Consulta sobre plan', status: 'closed', priority: 'low', createdAt: '2025-05-05T09:15:00Z' }
            ],
            totalCount: 17,
            page: 1,
            pageSize: 10
        };
    }

    // Base de conocimientos
    if (endpoint.includes('/knowledge')) {
        return {
            data: [
                { id: 'kb1', title: 'Cómo configurar su router', content: 'Contenido del artículo...', category: 'network' },
                { id: 'kb2', title: 'Métodos de pago disponibles', content: 'Contenido del artículo...', category: 'billing' },
                { id: 'kb3', title: 'Solución a problemas comunes', content: 'Contenido del artículo...', category: 'support' }
            ],
            totalCount: 12,
            page: 1,
            pageSize: 10
        };
    }

    // Inventario
    if (endpoint.includes('/inventory')) {
        return {
            data: [
                { id: 'item1', name: 'Router MikroTik', category: 'routers', stock: 15, price: 85.00 },
                { id: 'item2', name: 'Antena Ubiquiti', category: 'antennas', stock: 8, price: 120.00 },
                { id: 'item3', name: 'Cable de red Cat 6', category: 'cables', stock: 200, price: 2.50 }
            ],
            totalCount: 68,
            page: 1,
            pageSize: 10
        };
    }

    // Notificaciones para clientes
    if (endpoint.includes('/notifications')) {
        return [
            { id: 'notif1', title: 'Factura generada', message: 'Se ha generado su factura mensual', type: 'invoice', read: false },
            { id: 'notif2', title: 'Pago recibido', message: 'Hemos recibido su pago', type: 'payment', read: true },
            { id: 'notif3', title: 'Respuesta a ticket', message: 'Su ticket ha sido actualizado', type: 'support', read: false }
        ];
    }

    // Datos de tráfico
    if (endpoint.includes('/traffic')) {
        return {
            daily: [
                { date: '2025-05-01', download: 2.4, upload: 0.8 },
                { date: '2025-05-02', download: 3.1, upload: 1.2 },
                { date: '2025-05-03', download: 1.9, upload: 0.6 },
                { date: '2025-05-04', download: 4.2, upload: 1.5 },
                { date: '2025-05-05', download: 2.8, upload: 0.9 }
            ],
            monthly: [
                { month: '2025-01', download: 45.6, upload: 15.2 },
                { month: '2025-02', download: 52.3, upload: 17.8 },
                { month: '2025-03', download: 48.1, upload: 16.3 },
                { month: '2025-04', download: 62.7, upload: 21.5 },
                { month: '2025-05', download: 30.2, upload: 10.1 }
            ]
        };
    }

    // Datos de perfil para cliente
    if (endpoint.includes('/profile')) {
        return {
            id: 'client1',
            firstName: 'Juan',
            lastName: 'Pérez',
            email: 'juan@example.com',
            phone: '555-123-4567',
            address: 'Calle Principal 123',
            plan: {
                name: 'Plan Básico',
                download: 10,
                upload: 5,
                price: 50.00
            },
            joinDate: '2023-01-15'
        };
    }

    // Datos genéricos para cualquier otro endpoint
    return {
        success: true,
        message: 'Datos simulados para desarrollo',
        simulatedEndpoint: endpoint,
        timestamp: new Date().toISOString()
    };
};

// -----------------------------------------------------------------------------
// FUNCIONES PRINCIPALES DE API
// -----------------------------------------------------------------------------

/**
 * Realiza una petición HTTP con autenticación para el panel de administración
 * @param {string} url - URL relativa (sin API_BASE_URL)
 * @param {Object} options - Opciones de fetch
 * @param {number} timeout - Tiempo límite en ms
 * @param {boolean} useCache - Si se debe usar caché
 * @returns {Promise<Response>} Promesa con la respuesta
 */
export const fetchWithAuth = async (url, options = {}, timeout = DEFAULT_TIMEOUT, useCache = true) => {
    // Crear clave de caché basada en URL y método
    const method = options.method || 'GET';
    const cacheKey = `${method}:${url}`;

    // Verificar si podemos devolver datos cacheados
    if (useCache && method === 'GET' && apiCache.has(cacheKey)) {
        const cachedData = apiCache.get(cacheKey);
        if (cachedData.expires > Date.now()) {
            console.log(`[API] Devolviendo datos cacheados para: ${url}`);
            return cachedData.response.clone(); // Devolver clon para mantener integridad
        } else {
            // Cache expirado, eliminar
            apiCache.delete(cacheKey);
        }
    }

    // Obtener token de autenticación
    const token = localStorage.getItem('token');

    // Log de diagnóstico
    console.log('[API] Petición con autenticación de admin:', {
        url,
        method,
        hasToken: !!token,
        tokenLength: token?.length
    });

    // Si no hay token y la ruta requiere autenticación, rechazar
    if (!token) {
        throw new Error('No hay token de autenticación. Por favor, inicie sesión nuevamente.');
    }

    // Configurar opciones por defecto
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache', // Prevenir caché del navegador
            'Pragma': 'no-cache'
        }
    };

    // Combinar opciones personalizadas con opciones por defecto
    const fetchOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers
        }
    };

    // Agregar timestamp para evitar caché en GET
    let urlWithNoCache = url;
    if (method === 'GET' && !url.includes('_nocache')) {
        const separator = url.includes('?') ? '&' : '?';
        urlWithNoCache = `${url}${separator}_nocache=${Date.now()}`;
    }

    // Realizar petición con timeout
    try {
        console.log(`[API] Enviando ${method} a ${API_BASE_URL}${urlWithNoCache}`);
        const fetchPromise = fetch(`${API_BASE_URL}${urlWithNoCache}`, fetchOptions);
        const response = await timeoutPromise(fetchPromise, timeout);

        // Si la respuesta indica un error de autenticación, limpiar token
        if (response.status === 401) {
            console.error('[API] Error 401: Token inválido o expirado');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
            throw new Error('Sesión expirada. Por favor inicie sesión nuevamente.');
        }

        // Si la respuesta no es exitosa (no es 2xx)
        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        // Guardar en caché si es GET y useCache está habilitado
        if (useCache && method === 'GET') {
            // Determinar tiempo de caché según tipo de endpoint
            let cacheTime = API_CACHE_TIME.default;
            if (url.includes('/clients')) cacheTime = API_CACHE_TIME.clients;
            if (url.includes('/traffic')) cacheTime = API_CACHE_TIME.traffic;
            if (url.includes('/dashboard')) cacheTime = API_CACHE_TIME.dashboard;

            // Guardar clon de la respuesta en caché
            apiCache.set(cacheKey, {
                response: response.clone(),
                expires: Date.now() + cacheTime
            });
        }

        return response;
    } catch (error) {
        console.error(`[API] Error en ${method} ${url}:`, error);

        // Si es un error de timeout o red, intentar despejar la caché
        if (error.name === 'AbortError' || error.message.includes('network') || error.message.includes('timeout')) {
            clearCache(cacheKey);
        }

        throw error;
    }
};

/**
 * Realiza una petición HTTP con autenticación para el portal del cliente
 * @param {string} url - URL relativa (sin API_BASE_URL)
 * @param {Object} options - Opciones de fetch
 * @param {number} timeout - Tiempo límite en ms
 * @returns {Promise<Response>} Promesa con la respuesta
 */
export const fetchWithClientAuth = async (url, options = {}, timeout = DEFAULT_TIMEOUT) => {
    // Obtener token de autenticación de cliente
    const clientToken = localStorage.getItem('clientToken');

    // Log de diagnóstico
    console.log('[API] Petición con autenticación de cliente:', {
        url,
        method: options.method || 'GET',
        hasToken: !!clientToken,
        tokenLength: clientToken?.length
    });

    // Si no hay token y la ruta requiere autenticación, rechazar
    if (!clientToken) {
        throw new Error('No hay token de autenticación de cliente. Por favor, inicie sesión nuevamente.');
    }

    // Configurar opciones por defecto
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${clientToken}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
        }
    };

    // Combinar opciones personalizadas con opciones por defecto
    const fetchOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers
        }
    };

    // Agregar timestamp para evitar caché en GET
    let urlWithNoCache = url;
    const method = options.method || 'GET';
    if (method === 'GET' && !url.includes('_nocache')) {
        const separator = url.includes('?') ? '&' : '?';
        urlWithNoCache = `${url}${separator}_nocache=${Date.now()}`;
    }

    // Realizar petición con timeout
    try {
        console.log(`[API] Enviando ${method} a ${API_BASE_URL}${urlWithNoCache}`);
        const fetchPromise = fetch(`${API_BASE_URL}${urlWithNoCache}`, fetchOptions);
        const response = await timeoutPromise(fetchPromise, timeout);

        // Si la respuesta indica un error de autenticación, limpiar token
        if (response.status === 401) {
            console.error('[API] Error 401: Token de cliente inválido o expirado');
            localStorage.removeItem('clientToken');
            localStorage.removeItem('clientData');
            localStorage.removeItem('clientServices');
            if (!window.location.pathname.includes('/client/login')) {
                window.location.href = '/client/login';
            }
            throw new Error('Sesión expirada. Por favor inicie sesión nuevamente.');
        }

        // Si la respuesta no es exitosa (no es 2xx)
        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('[API] Error en petición de cliente:', error);
        throw error;
    }
};

// -----------------------------------------------------------------------------
// MÉTODOS HTTP DE ALTO NIVEL
// -----------------------------------------------------------------------------

/**
 * Realiza una petición GET
 * @param {string} url - URL relativa
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @param {number} timeout - Tiempo límite en ms
 * @param {boolean} useCache - Si se debe usar caché
 * @returns {Promise<Object>} Promesa con datos JSON
 */
export const get = async (url, isClient = false, timeout = DEFAULT_TIMEOUT, useCache = true) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] GET ${apiUrl} (${isClient ? 'cliente' : 'admin'})`);

        // MODO DEBUG: Devolver datos simulados sin llamar a la API real
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Modo de depuración activado para ${apiUrl}`);
            return simulateFetchData(apiUrl);
        }

        // Verificar autenticación antes de proceder
        if (isClient && !hasValidClientToken()) {
            console.error('[API] Intento de solicitud cliente sin token válido');
            throw new Error('No hay una sesión de cliente válida. Por favor, inicie sesión nuevamente.');
        } else if (!isClient && !hasValidAdminToken()) {
            console.error('[API] Intento de solicitud admin sin token válido');
            throw new Error('No hay una sesión de administrador válida. Por favor, inicie sesión nuevamente.');
        }

        const fetchFn = isClient ? fetchWithClientAuth : fetchWithAuth;
        const response = await fetchFn(apiUrl, {}, timeout, useCache);

        // Procesar respuesta
        return await response.json();
    } catch (error) {
        console.error(`[API] Error en GET ${url}:`, error);

        // En modo debug, devolver datos simulados incluso en caso de error
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Devolviendo datos simulados después de error en ${url}`);
            let apiUrl = url;
            if (!url.startsWith('/api/')) {
                apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
            }
            return simulateFetchData(apiUrl);
        }

        throw error;
    }
};

/**
 * Realiza una petición POST
 * @param {string} url - URL relativa
 * @param {Object} data - Datos a enviar
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @param {number} timeout - Tiempo límite en ms
 * @returns {Promise<Object>} Promesa con datos JSON
 */
export const post = async (url, data, isClient = false, timeout = DEFAULT_TIMEOUT) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] POST ${apiUrl} (${isClient ? 'cliente' : 'admin'})`, data);

        // MODO DEBUG: Devolver datos simulados sin llamar a la API real
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Modo de depuración activado para POST ${apiUrl}`);
            return simulateFetchData(apiUrl);
        }

        // Verificar autenticación antes de proceder
        if (isClient && !hasValidClientToken()) {
            console.error('[API] Intento de solicitud POST cliente sin token válido');
            throw new Error('No hay una sesión de cliente válida. Por favor, inicie sesión nuevamente.');
        } else if (!isClient && !hasValidAdminToken()) {
            console.error('[API] Intento de solicitud POST admin sin token válido');
            throw new Error('No hay una sesión de administrador válida. Por favor, inicie sesión nuevamente.');
        }

        const fetchFn = isClient ? fetchWithClientAuth : fetchWithAuth;
        const response = await fetchFn(
            apiUrl,
            {
                method: 'POST',
                body: JSON.stringify(data)
            },
            timeout,
            false // No usar caché para POST
        );

        return await response.json();
    } catch (error) {
        console.error(`[API] Error en POST ${url}:`, error);

        // En modo debug, devolver datos simulados incluso en caso de error
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Devolviendo datos simulados después de error en POST ${url}`);
            let apiUrl = url;
            if (!url.startsWith('/api/')) {
                apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
            }
            return simulateFetchData(apiUrl);
        }

        throw error;
    }
};

/**
 * Realiza una petición PUT
 * @param {string} url - URL relativa
 * @param {Object} data - Datos a enviar
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @param {number} timeout - Tiempo límite en ms
 * @returns {Promise<Object>} Promesa con datos JSON
 */
export const put = async (url, data, isClient = false, timeout = DEFAULT_TIMEOUT) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] PUT ${apiUrl} (${isClient ? 'cliente' : 'admin'})`, data);

        // MODO DEBUG: Devolver datos simulados sin llamar a la API real
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Modo de depuración activado para PUT ${apiUrl}`);
            return simulateFetchData(apiUrl);
        }

        // Verificar autenticación antes de proceder
        if (isClient && !hasValidClientToken()) {
            console.error('[API] Intento de solicitud PUT cliente sin token válido');
            throw new Error('No hay una sesión de cliente válida. Por favor, inicie sesión nuevamente.');
        } else if (!isClient && !hasValidAdminToken()) {
            console.error('[API] Intento de solicitud PUT admin sin token válido');
            throw new Error('No hay una sesión de administrador válida. Por favor, inicie sesión nuevamente.');
        }

        const fetchFn = isClient ? fetchWithClientAuth : fetchWithAuth;
        const response = await fetchFn(
            apiUrl,
            {
                method: 'PUT',
                body: JSON.stringify(data)
            },
            timeout,
            false // No usar caché para PUT
        );

        return await response.json();
    } catch (error) {
        console.error(`[API] Error en PUT ${url}:`, error);

        // En modo debug, devolver datos simulados incluso en caso de error
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Devolviendo datos simulados después de error en PUT ${url}`);
            let apiUrl = url;
            if (!url.startsWith('/api/')) {
                apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
            }
            return simulateFetchData(apiUrl);
        }

        throw error;
    }
};

/**
 * Realiza una petición DELETE
 * @param {string} url - URL relativa
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @param {number} timeout - Tiempo límite en ms
 * @returns {Promise<Object|null>} Promesa con datos JSON o null
 */
export const del = async (url, isClient = false, timeout = DEFAULT_TIMEOUT) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] DELETE ${apiUrl} (${isClient ? 'cliente' : 'admin'})`);

        // MODO DEBUG: Devolver datos simulados sin llamar a la API real
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Modo de depuración activado para DELETE ${apiUrl}`);
            return { success: true, message: "Eliminado con éxito (simulado)" };
        }

        // Verificar autenticación antes de proceder
        if (isClient && !hasValidClientToken()) {
            console.error('[API] Intento de solicitud DELETE cliente sin token válido');
            throw new Error('No hay una sesión de cliente válida. Por favor, inicie sesión nuevamente.');
        } else if (!isClient && !hasValidAdminToken()) {
            console.error('[API] Intento de solicitud DELETE admin sin token válido');
            throw new Error('No hay una sesión de administrador válida. Por favor, inicie sesión nuevamente.');
        }

        const fetchFn = isClient ? fetchWithClientAuth : fetchWithAuth;
        const response = await fetchFn(
            apiUrl,
            {
                method: 'DELETE'
            },
            timeout,
            false // No usar caché para DELETE
        );

        // Algunas respuestas DELETE pueden no devolver contenido
        if (response.status === 204) {
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error(`[API] Error en DELETE ${url}:`, error);

        // En modo debug, devolver datos simulados incluso en caso de error
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Devolviendo datos simulados después de error en DELETE ${url}`);
            return { success: true, message: "Eliminado con éxito (simulado tras error)" };
        }

        throw error;
    }
};

// -----------------------------------------------------------------------------
// FUNCIONES PARA MANEJO DE ARCHIVOS
// -----------------------------------------------------------------------------

/**
 * Envía datos de formulario multipart (para subir archivos)
 * @param {string} url - URL relativa
 * @param {FormData} formData - Datos del formulario
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @param {number} timeout - Tiempo límite en ms
 * @returns {Promise<Object>} Promesa con datos JSON
 */
export const uploadFormData = async (url, formData, isClient = false, timeout = DEFAULT_TIMEOUT * 2) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] Subiendo archivo a ${apiUrl} (${isClient ? 'cliente' : 'admin'})`);

        // MODO DEBUG: Devolver datos simulados sin llamar a la API real
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Modo de depuración activado para subida de archivo ${apiUrl}`);
            return { success: true, fileUrl: "https://example.com/uploads/simulado.jpg" };
        }

        const token = isClient
            ? localStorage.getItem('clientToken')
            : localStorage.getItem('token');

        if (!token) {
            throw new Error(`No hay token de autenticación ${isClient ? 'de cliente' : ''}`);
        }

        // No incluir Content-Type para que el navegador establezca el boundary correcto
        const options = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            },
            body: formData
        };

        const fetchPromise = fetch(`${API_BASE_URL}${apiUrl}`, options);
        const response = await timeoutPromise(fetchPromise, timeout);

        if (response.status === 401) {
            if (isClient) {
                localStorage.removeItem('clientToken');
                localStorage.removeItem('clientData');
                localStorage.removeItem('clientServices');
                if (!window.location.pathname.includes('/client/login')) {
                    window.location.href = '/client/login';
                }
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                if (!window.location.pathname.includes('/login')) {
                    window.location.href = '/login';
                }
            }
            throw new Error('Sesión expirada. Por favor inicie sesión nuevamente.');
        }

        // Si la respuesta no es exitosa (no es 2xx)
        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('[API] Error en la subida de archivos:', error);

        // En modo debug, devolver datos simulados incluso en caso de error
        if (API_DEBUG_MODE) {
            console.log(`[API DEBUG] Devolviendo datos simulados después de error en subida de archivo`);
            return { success: true, fileUrl: "https://example.com/uploads/simulado-error.jpg" };
        }

        throw error;
    }
};

/**
 * Descarga un archivo
 * @param {string} url - URL relativa del archivo
 * @param {string} filename - Nombre para guardar el archivo
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @returns {Promise<void>}
 */
export const downloadFile = async (url, filename, isClient = false) => {
    try {
        // Asegurarse de que la URL siempre empiece con /api/
        let apiUrl = url;
        if (!url.startsWith('/api/')) {
            apiUrl = `/api${url.startsWith('/') ? url : `/${url}`}`;
        }

        console.log(`[API] Descargando archivo desde ${apiUrl} (${isClient ? 'cliente' : 'admin'})`);

        // Obtener token según tipo de usuario
        const token = isClient
            ? localStorage.getItem('clientToken')
            : localStorage.getItem('token');

        if (!token) {
            throw new Error(`No hay token de autenticación ${isClient ? 'de cliente' : ''}`);
        }

        // Realizar la petición
        const response = await fetch(`${API_BASE_URL}${apiUrl}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        // Convertir la respuesta a blob
        const blob = await response.blob();

        // Crear URL para el blob
        const url = window.URL.createObjectURL(blob);

        // Crear un elemento <a> para descargar el archivo
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;

        // Añadir al DOM, hacer clic y luego eliminar
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error('[API] Error al descargar archivo:', error);
        throw error;
    }
};

// -----------------------------------------------------------------------------
// FUNCIONES DE UTILIDAD Y DIAGNÓSTICO
// -----------------------------------------------------------------------------

/**
 * Fuerza una recarga de datos limpiando la caché para un endpoint
 * @param {string} url - URL relativa del endpoint a recargar
 * @param {boolean} isClient - Si es petición del portal de cliente
 * @returns {Promise<Object>} - Promesa con los nuevos datos
 */
export const forceRefresh = async (url, isClient = false) => {
    // Limpiar caché para este endpoint
    const cacheKey = `GET:${url}`;
    clearCache(cacheKey);

    // Hacer una nueva petición sin usar caché
    return await get(url, isClient, DEFAULT_TIMEOUT, false);
};

/**
 * Función de diagnóstico para verificar problemas de API
 * @returns {Object} - Resultado del diagnóstico
 */
export const diagnoseApiConnection = async () => {
    const results = {
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV,
        apiUrl: API_BASE_URL,
        adminAuthStatus: {
            hasToken: hasValidAdminToken(),
            tokenLength: localStorage.getItem('token')?.length || 0
        },
        clientAuthStatus: {
            hasToken: hasValidClientToken(),
            tokenLength: localStorage.getItem('clientToken')?.length || 0
        },
        tests: []
    };

    // Probar conexión sin autenticación
    try {
        const response = await fetch(`${API_BASE_URL}/api/health`);
        results.tests.push({
            name: 'API Health Check',
            success: response.ok,
            status: response.status,
            statusText: response.statusText
        });
    } catch (error) {
        results.tests.push({
            name: 'API Health Check',
            success: false,
            error: error.message
        });
    }

    // Probar endpoint de autenticación
    if (results.adminAuthStatus.hasToken) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            results.tests.push({
                name: 'Admin Token Verification',
                success: response.ok,
                status: response.status,
                statusText: response.statusText
            });
        } catch (error) {
            results.tests.push({
                name: 'Admin Token Verification',
                success: false,
                error: error.message
            });
        }
    }

    // Probar endpoint de cliente
    if (results.clientAuthStatus.hasToken) {
        try {
            const response = await fetch(`${API_BASE_URL}/api/client/auth/verify`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('clientToken')}`
                }
            });
            results.tests.push({
                name: 'Client Token Verification',
                success: response.ok,
                status: response.status,
                statusText: response.statusText
            });
        } catch (error) {
            results.tests.push({
                name: 'Client Token Verification',
                success: false,
                error: error.message
            });
        }
    }

    console.log('[API] Resultados de diagnóstico:', results);
    return results;
};

// Exportar todas las funciones
export default {
    // Funciones principales
    get,
    post,
    put,
    del,
    uploadFormData,
    downloadFile,

    // Funciones de utilidad
    clearCache,
    forceRefresh,
    diagnoseApiConnection,

    // Verificación de autenticación
    hasValidAdminToken,
    hasValidClientToken,

    // Configuración
    API_DEBUG_MODE
};